package data.datasource.remote

import data.graphql.GitHubGraphQLQueries
import data.network.AuthenticatedHttpClient
import domain.model.*
import io.ktor.client.call.*
import io.ktor.client.request.*
import io.ktor.http.*
import kotlinx.coroutines.delay
import kotlinx.serialization.Serializable
import kotlinx.serialization.Contextual
import kotlinx.serialization.json.*

/**
 * Core GraphQL client using Ktor HTTP via Vercel API proxy
 * Handles query execution, JSON response parsing, and error handling
 * Uses Vercel API endpoints to avoid CSP issues and provide session management
 */
class GitHubGraphQLRemoteDataSource(
    private val authenticatedHttpClient: AuthenticatedHttpClient
) {
    private val graphqlEndpoint = "/api/github/graphql"
    
    /**
     * Execute a GraphQL query with variables via our Vercel proxy
     */
    suspend fun executeGraphQLQuery(
        query: String,
        variables: Map<String, Any?> = emptyMap()
    ): GraphQLResponse {
        try {
            val response = authenticatedHttpClient.authenticatedRequest {
                url(graphqlEndpoint)
                method = HttpMethod.Post
                contentType(ContentType.Application.Json)
                // Convert variables to strings for Edge Function compatibility
                val stringVariables = variables.mapValues { (_, value) ->
                    when (value) {
                        null -> null
                        is String -> value
                        else -> value.toString()
                    }
                }.filterValues { it != null }
                setBody(GraphQLRequest(query, stringVariables))
            }
            
            if (response.status.isSuccess()) {
                return response.body<GraphQLResponse>()
            } else {
                throw Exception("GraphQL request failed with status: ${response.status}")
            }
        } catch (e: Exception) {
            println("🚫 GraphQL Error: ${e.message}")
            throw GraphQLException("Failed to execute GraphQL query", e)
        }
    }
    
    /**
     * Get user's organizations with SSO and membership details
     */
    suspend fun getUserOrganizations(): List<OrganizationInsights> {
        val organizations = mutableListOf<OrganizationInsights>()
        var cursor: String? = null
        var hasNextPage = true
        
        while (hasNextPage) {
            try {
                val variables = GitHubGraphQLQueries.buildQueryVariables(
                    "cursor" to cursor
                )
                
                val response = executeGraphQLQuery(
                    GitHubGraphQLQueries.GET_USER_ORGANIZATIONS,
                    variables
                )
                
                val data = response.data?.jsonObject
                val viewer = data?.get("viewer")?.jsonObject
                val orgsConnection = viewer?.get("organizations")?.jsonObject
                val edges = orgsConnection?.get("edges")?.jsonArray
                
                edges?.forEach { edge ->
                    val edgeObj = edge.jsonObject
                    val orgNode = edgeObj["node"]?.jsonObject
                    // Note: role field is not available on OrganizationEdge
                    // Individual organization role queries would be needed for role info
                    
                    if (orgNode != null) {
                        val orgInsights = parseOrganizationInsights(orgNode, null)
                        organizations.add(orgInsights)
                    }
                }
                
                val pageInfo = orgsConnection?.get("pageInfo")?.jsonObject
                hasNextPage = pageInfo?.get("hasNextPage")?.jsonPrimitive?.boolean ?: false
                cursor = pageInfo?.get("endCursor")?.jsonPrimitive?.contentOrNull
                
            } catch (e: Exception) {
                println("🚫 Error fetching user organizations: ${e.message}")
                break
            }
        }
        
        return organizations
    }
    
    /**
     * Diagnose organization access issues
     */
    suspend fun diagnoseOrganizationAccess(): OrganizationAccessDiagnosis {
        try {
            val response = executeGraphQLQuery(GitHubGraphQLQueries.GET_USER_ORGANIZATION_MEMBERSHIPS)
            
            val data = response.data?.jsonObject
            val viewer = data?.get("viewer")?.jsonObject
            
            // Get basic user info
            val userLogin = viewer?.get("login")?.jsonPrimitive?.content ?: "unknown"
            val verifiedEmails = viewer?.get("organizationVerifiedDomainEmails")?.jsonArray?.map { 
                it.jsonPrimitive.content 
            } ?: emptyList()
            
            // Get visible organizations
            val visibleOrgs = getUserOrganizations()
            
            return OrganizationAccessDiagnosis(
                userLogin = userLogin,
                visibleOrganizationCount = visibleOrgs.size,
                visibleOrganizations = visibleOrgs.map { it.organization.login },
                verifiedDomainEmails = verifiedEmails,
                recommendations = generateAccessRecommendations(visibleOrgs.size, verifiedEmails)
            )
            
        } catch (e: Exception) {
            println("🚫 Error diagnosing organization access: ${e.message}")
            return OrganizationAccessDiagnosis(
                userLogin = "unknown",
                visibleOrganizationCount = 0,
                visibleOrganizations = emptyList(),
                verifiedDomainEmails = emptyList(),
                recommendations = listOf("Error occurred during diagnosis. Please check your authentication.")
            )
        }
    }
    
    private fun generateAccessRecommendations(visibleOrgCount: Int, verifiedEmails: List<String>): List<String> {
        val recommendations = mutableListOf<String>()
        
        if (visibleOrgCount < 2) {
            recommendations.add("🔐 You're only seeing $visibleOrgCount organization(s). This suggests OAuth app restrictions.")
            recommendations.add("📋 Visit https://github.com/settings/applications to see authorized apps")
            recommendations.add("🏢 Ask organization admins to approve this OAuth application")
            recommendations.add("⚙️ Check organization settings: Settings > Third-party access > OAuth application policy")
        }
        
        if (verifiedEmails.isNotEmpty()) {
            recommendations.add("✅ You have verified domain emails: ${verifiedEmails.joinToString(", ")}")
            recommendations.add("📧 Organizations matching these domains may require SAML SSO")
        }
        
        recommendations.add("🔄 Try re-authorizing the application with expanded scopes")
        recommendations.add("🎯 Ensure the OAuth app requests 'read:org' scope for organization access")
        
        return recommendations
    }
    
    /**
     * Get pull requests with enhanced insights across organizations
     */
    suspend fun getPullRequestsInsights(
        searchQuery: String,
        maxResults: Int = 100
    ): List<PullRequestInsights> {
        val pullRequests = mutableListOf<PullRequestInsights>()
        var cursor: String? = null
        var hasNextPage = true
        var fetchedCount = 0
        
        while (hasNextPage && fetchedCount < maxResults) {
            try {
                val variables = GitHubGraphQLQueries.buildQueryVariables(
                    "query" to searchQuery,
                    "cursor" to cursor
                )
                
                val response = executeGraphQLQuery(
                    GitHubGraphQLQueries.GET_PULL_REQUESTS_INSIGHTS,
                    variables
                )
                
                val data = response.data?.jsonObject
                val search = data?.get("search")?.jsonObject
                val edges = search?.get("edges")?.jsonArray
                
                edges?.forEach { edge ->
                    val node = edge.jsonObject["node"]?.jsonObject
                    if (node != null && fetchedCount < maxResults) {
                        val prInsights = parsePullRequestInsights(node)
                        pullRequests.add(prInsights)
                        fetchedCount++
                    }
                }
                
                val pageInfo = search?.get("pageInfo")?.jsonObject
                hasNextPage = pageInfo?.get("hasNextPage")?.jsonPrimitive?.boolean ?: false
                cursor = pageInfo?.get("endCursor")?.jsonPrimitive?.contentOrNull
                
                // Add small delay to avoid rate limiting
                if (hasNextPage) {
                    delay(100)
                }
                
            } catch (e: Exception) {
                println("🚫 Error fetching PR insights: ${e.message}")
                break
            }
        }
        
        return pullRequests
    }
    
    /**
     * Get detailed organization information
     */
    suspend fun getOrganizationDetails(login: String): OrganizationInsights? {
        try {
            val variables = GitHubGraphQLQueries.buildQueryVariables(
                "login" to login
            )
            
            val response = executeGraphQLQuery(
                GitHubGraphQLQueries.GET_ORGANIZATION_DETAILS,
                variables
            )
            
            val data = response.data?.jsonObject
            val organization = data?.get("organization")?.jsonObject
            
            return if (organization != null) {
                parseOrganizationInsights(organization, null)
            } else {
                null
            }
        } catch (e: Exception) {
            println("🚫 Error fetching organization details for $login: ${e.message}")
            return null
        }
    }
    
    /**
     * Get pending organization invitations
     * Note: Individual user invitations are not accessible via GraphQL.
     * Returns empty list for API compatibility.
     */
    suspend fun getPendingInvitations(): List<OrganizationInvitation> {
        try {
            val response = executeGraphQLQuery(GitHubGraphQLQueries.GET_PENDING_INVITATIONS)
            
            // Since organizationInvitations field doesn't exist on User type,
            // we return an empty list. Organization invitations would need to be
            // accessed through the organization's invitation management APIs.
            println("⚠️ Organization invitations are not accessible via GraphQL on User type")
            return emptyList()
            
        } catch (e: Exception) {
            println("🚫 Error fetching pending invitations: ${e.message}")
            return emptyList()
        }
    }
    
    /**
     * Get user's team memberships
     */
    suspend fun getUserTeams(): List<Team> {
        val teams = mutableListOf<Team>()
        var cursor: String? = null
        var hasNextPage = true
        
        while (hasNextPage) {
            try {
                val variables = GitHubGraphQLQueries.buildQueryVariables(
                    "cursor" to cursor
                )
                
                val response = executeGraphQLQuery(
                    GitHubGraphQLQueries.GET_USER_TEAMS,
                    variables
                )
                
                val data = response.data?.jsonObject
                val viewer = data?.get("viewer")?.jsonObject
                val organizationsConnection = viewer?.get("organizations")?.jsonObject
                val orgNodes = organizationsConnection?.get("nodes")?.jsonArray
                
                // Parse teams from each organization
                orgNodes?.forEach { orgNode ->
                    val orgObject = orgNode.jsonObject
                    val teamsConnection = orgObject["teams"]?.jsonObject
                    val teamNodes = teamsConnection?.get("nodes")?.jsonArray
                    
                    teamNodes?.forEach { teamNode ->
                        val teamObject = teamNode.jsonObject
                        // Parse team with organization context
                        val team = parseTeamWithOrganization(teamObject, orgObject)
                        teams.add(team)
                    }
                }
                
                val pageInfo = organizationsConnection?.get("pageInfo")?.jsonObject
                hasNextPage = pageInfo?.get("hasNextPage")?.jsonPrimitive?.boolean ?: false
                cursor = pageInfo?.get("endCursor")?.jsonPrimitive?.contentOrNull
                
            } catch (e: Exception) {
                println("🚫 Error fetching user teams: ${e.message}")
                break
            }
        }
        
        return teams
    }
    
    /**
     * Check SSO authentication status for an organization
     */
    suspend fun getSSOStatus(organizationLogin: String): SSOAuthStatus? {
        try {
            val variables = GitHubGraphQLQueries.buildQueryVariables(
                "organizationLogin" to organizationLogin
            )
            
            val response = executeGraphQLQuery(
                GitHubGraphQLQueries.GET_SSO_STATUS,
                variables
            )
            
            val data = response.data?.jsonObject
            val organization = data?.get("organization")?.jsonObject
            val samlProvider = organization?.get("samlIdentityProvider")?.jsonObject
            
            return if (samlProvider != null) {
                SSOAuthStatus(
                    isAuthenticated = organization["viewerIsAMember"]?.jsonPrimitive?.boolean ?: false,
                    expiresAt = null, // Not available in GraphQL
                    lastAuthenticated = null, // Not available in GraphQL
                    authUrl = samlProvider["ssoUrl"]?.jsonPrimitive?.contentOrNull,
                    requiresReauth = !(organization["viewerCanCreateRepositories"]?.jsonPrimitive?.boolean ?: false)
                )
            } else {
                null
            }
        } catch (e: Exception) {
            println("🚫 Error fetching SSO status for $organizationLogin: ${e.message}")
            return null
        }
    }
    
    // Parsing methods
    private fun parseOrganizationInsights(orgJson: JsonObject, role: String?): OrganizationInsights {
        val organization = GitHubOrganization(
            id = orgJson["id"]?.jsonPrimitive?.content ?: "",
            login = orgJson["login"]?.jsonPrimitive?.content ?: "",
            name = orgJson["name"]?.jsonPrimitive?.contentOrNull,
            description = orgJson["description"]?.jsonPrimitive?.contentOrNull,
            avatarUrl = orgJson["avatarUrl"]?.jsonPrimitive?.content ?: "",
            htmlUrl = orgJson["url"]?.jsonPrimitive?.content ?: "",
            location = orgJson["location"]?.jsonPrimitive?.contentOrNull,
            email = orgJson["email"]?.jsonPrimitive?.contentOrNull,
            twitterUsername = orgJson["twitterUsername"]?.jsonPrimitive?.contentOrNull,
            blog = orgJson["websiteUrl"]?.jsonPrimitive?.contentOrNull,
            company = null,
            createdAt = orgJson["createdAt"]?.jsonPrimitive?.content ?: "",
            updatedAt = orgJson["updatedAt"]?.jsonPrimitive?.content ?: "",
            publicRepos = orgJson["repositories"]?.jsonObject?.get("totalCount")?.jsonPrimitive?.int ?: 0,
            privateRepos = orgJson["repositories"]?.jsonObject?.get("totalCount")?.jsonPrimitive?.int ?: 0,
            totalPrivateRepos = orgJson["repositories"]?.jsonObject?.get("totalCount")?.jsonPrimitive?.int ?: 0,
            publicGists = 0,
            followers = orgJson["membersWithRole"]?.jsonObject?.get("totalCount")?.jsonPrimitive?.int ?: 0,
            following = 0,
            type = "Organization",
            membership = role?.let { 
                OrganizationMembership(
                    role = it,
                    state = "active",
                    url = "",
                    organizationUrl = orgJson["url"]?.jsonPrimitive?.content ?: "",
                    permissions = MembershipPermissions(
                        canCreateRepository = true,
                        canCreatePrivateRepository = true,
                        canCreatePublicRepository = true,
                        canCreateTeam = true
                    )
                )
            },
            ssoProvider = orgJson["samlIdentityProvider"]?.jsonObject?.let { saml ->
                SSOProvider(
                    enabled = true,
                    provider = "saml",
                    issuer = null,
                    loginUrl = saml["ssoUrl"]?.jsonPrimitive?.contentOrNull,
                    certificateFingerprint = null
                )
            },
            requiresSAML = false, // Remove this field since it's not available
            isVerified = orgJson["isVerified"]?.jsonPrimitive?.boolean ?: false,
            pendingInvitations = emptyList()
        )
        
        val teams = orgJson["teams"]?.jsonObject?.get("nodes")?.jsonArray?.map { teamNode ->
            parseTeam(teamNode.jsonObject)
        } ?: emptyList()
        
        val activity = OrganizationActivity(
            totalPullRequests = 0,
            openPullRequests = 0,
            totalIssues = 0,
            openIssues = 0,
            lastActivityAt = null
        )
        
        val accessLevel = when (role?.uppercase()) {
            "OWNER" -> OrganizationAccessLevel.OWNER
            "ADMIN" -> OrganizationAccessLevel.ADMIN
            "MEMBER" -> OrganizationAccessLevel.MEMBER
            else -> OrganizationAccessLevel.NONE
        }
        
        return OrganizationInsights(
            organization = organization,
            ssoStatus = null,
            teams = teams,
            recentActivity = activity,
            accessLevel = accessLevel
        )
    }
    
    private fun parsePullRequestInsights(prJson: JsonObject): PullRequestInsights {
        val repository = Repository(
            id = prJson["repository"]?.jsonObject?.get("id")?.jsonPrimitive?.content ?: "",
            name = prJson["repository"]?.jsonObject?.get("name")?.jsonPrimitive?.content ?: "",
            nameWithOwner = prJson["repository"]?.jsonObject?.get("nameWithOwner")?.jsonPrimitive?.content ?: "",
            htmlUrl = prJson["repository"]?.jsonObject?.get("url")?.jsonPrimitive?.content ?: "",
            isPrivate = prJson["repository"]?.jsonObject?.get("isPrivate")?.jsonPrimitive?.boolean ?: false,
            primaryLanguage = prJson["repository"]?.jsonObject?.get("primaryLanguage")?.jsonObject?.get("name")?.jsonPrimitive?.contentOrNull,
            organization = prJson["repository"]?.jsonObject?.get("owner")?.jsonObject?.let { owner ->
                RepositoryOrganization(
                    login = owner["login"]?.jsonPrimitive?.content ?: "",
                    name = owner["name"]?.jsonPrimitive?.contentOrNull,
                    avatarUrl = owner["avatarUrl"]?.jsonPrimitive?.content ?: ""
                )
            }
        )
        
        return PullRequestInsights(
            id = prJson["id"]?.jsonPrimitive?.content ?: "",
            number = prJson["number"]?.jsonPrimitive?.int ?: 0,
            title = prJson["title"]?.jsonPrimitive?.content ?: "",
            htmlUrl = prJson["url"]?.jsonPrimitive?.content ?: "",
            state = prJson["state"]?.jsonPrimitive?.content ?: "",
            isDraft = prJson["isDraft"]?.jsonPrimitive?.boolean ?: false,
            createdAt = prJson["createdAt"]?.jsonPrimitive?.content ?: "",
            updatedAt = prJson["updatedAt"]?.jsonPrimitive?.content ?: "",
            author = parseAuthor(prJson["author"]?.jsonObject),
            repository = repository,
            labels = prJson["labels"]?.jsonObject?.get("nodes")?.jsonArray?.map { labelNode ->
                parseLabel(labelNode.jsonObject)
            } ?: emptyList(),
            reviewDecision = prJson["reviewDecision"]?.jsonPrimitive?.contentOrNull,
            totalCommentsCount = prJson["comments"]?.jsonObject?.get("totalCount")?.jsonPrimitive?.int ?: 0,
            additions = prJson["additions"]?.jsonPrimitive?.int ?: 0,
            deletions = prJson["deletions"]?.jsonPrimitive?.int ?: 0,
            changedFiles = prJson["changedFiles"]?.jsonPrimitive?.int ?: 0,
            mergeable = prJson["mergeable"]?.jsonPrimitive?.contentOrNull,
            baseRefName = prJson["baseRefName"]?.jsonPrimitive?.content ?: "",
            headRefName = prJson["headRefName"]?.jsonPrimitive?.content ?: "",
            organization = repository.organization?.let { orgRepo ->
                // This would need to be enriched with full organization data
                null // Placeholder for now
            },
            teams = emptyList(), // Would need additional query to get teams
            assignees = prJson["assignees"]?.jsonObject?.get("nodes")?.jsonArray?.map { assigneeNode ->
                parseAuthor(assigneeNode.jsonObject)
            } ?: emptyList(),
            reviewRequests = prJson["reviewRequests"]?.jsonObject?.get("nodes")?.jsonArray?.map { requestNode ->
                ReviewRequest(
                    requestedReviewer = parseAuthor(requestNode.jsonObject["requestedReviewer"]?.jsonObject)
                )
            } ?: emptyList(),
            reviews = prJson["reviews"]?.jsonObject?.get("nodes")?.jsonArray?.map { reviewNode ->
                parseReview(reviewNode.jsonObject)
            } ?: emptyList()
        )
    }
    
    private fun parseAuthor(authorJson: JsonObject?): Author {
        return Author(
            login = authorJson?.get("login")?.jsonPrimitive?.content ?: "",
            name = authorJson?.get("name")?.jsonPrimitive?.contentOrNull,
            avatarUrl = authorJson?.get("avatarUrl")?.jsonPrimitive?.content ?: "",
            htmlUrl = authorJson?.get("url")?.jsonPrimitive?.content ?: ""
        )
    }
    
    private fun parseLabel(labelJson: JsonObject): Label {
        return Label(
            name = labelJson["name"]?.jsonPrimitive?.content ?: "",
            color = labelJson["color"]?.jsonPrimitive?.content ?: "",
            description = labelJson["description"]?.jsonPrimitive?.contentOrNull
        )
    }
    
    private fun parseTeam(teamJson: JsonObject): Team {
        return Team(
            id = teamJson["id"]?.jsonPrimitive?.content ?: "",
            name = teamJson["name"]?.jsonPrimitive?.content ?: "",
            slug = teamJson["slug"]?.jsonPrimitive?.content ?: "",
            description = teamJson["description"]?.jsonPrimitive?.contentOrNull,
            privacy = teamJson["privacy"]?.jsonPrimitive?.content ?: "",
            organization = TeamOrganization(
                login = teamJson["organization"]?.jsonObject?.get("login")?.jsonPrimitive?.content ?: "",
                name = teamJson["organization"]?.jsonObject?.get("name")?.jsonPrimitive?.contentOrNull
            )
        )
    }
    
    private fun parseTeamWithOrganization(teamJson: JsonObject, orgJson: JsonObject): Team {
        return Team(
            id = teamJson["id"]?.jsonPrimitive?.content ?: "",
            name = teamJson["name"]?.jsonPrimitive?.content ?: "",
            slug = teamJson["slug"]?.jsonPrimitive?.content ?: "",
            description = teamJson["description"]?.jsonPrimitive?.contentOrNull,
            privacy = teamJson["privacy"]?.jsonPrimitive?.content ?: "",
            organization = TeamOrganization(
                login = orgJson["login"]?.jsonPrimitive?.content ?: "",
                name = orgJson["name"]?.jsonPrimitive?.contentOrNull
            )
        )
    }
    
    private fun parseReview(reviewJson: JsonObject): Review {
        return Review(
            id = reviewJson["id"]?.jsonPrimitive?.content ?: "",
            author = parseAuthor(reviewJson["author"]?.jsonObject),
            state = reviewJson["state"]?.jsonPrimitive?.content ?: "",
            submittedAt = reviewJson["submittedAt"]?.jsonPrimitive?.contentOrNull
        )
    }
    
    private fun parseOrganizationInvitation(inviteJson: JsonObject): OrganizationInvitation {
        return OrganizationInvitation(
            id = inviteJson["id"]?.jsonPrimitive?.content ?: "",
            login = null,
            email = inviteJson["email"]?.jsonPrimitive?.contentOrNull,
            role = inviteJson["role"]?.jsonPrimitive?.content ?: "",
            teamCount = 0,
            invitationTeamUrl = null,
            invitationUrl = "",
            createdAt = inviteJson["createdAt"]?.jsonPrimitive?.content ?: "",
            inviter = OrganizationInviter(
                login = inviteJson["inviter"]?.jsonObject?.get("login")?.jsonPrimitive?.content ?: "",
                name = inviteJson["inviter"]?.jsonObject?.get("name")?.jsonPrimitive?.contentOrNull,
                avatarUrl = inviteJson["inviter"]?.jsonObject?.get("avatarUrl")?.jsonPrimitive?.content ?: "",
                htmlUrl = inviteJson["inviter"]?.jsonObject?.get("url")?.jsonPrimitive?.content ?: ""
            ),
            failedAt = null,
            failedReason = null
        )
    }
    
    /**
     * Get GitHub App installations accessible to the current user
     * This returns organizations where the GitHub App is installed
     */
    suspend fun getGitHubAppInstallations(): List<GitHubAppInstallation> {
        try {
            val response = authenticatedHttpClient.client.get("/api/github/installations") {
                headers {
                    append(HttpHeaders.Accept, "application/json")
                }
            }
            
            if (response.status == HttpStatusCode.OK) {
                val json = response.body<String>()
                val jsonElement = Json.parseToJsonElement(json)
                val installations = jsonElement.jsonObject["installations"]?.jsonArray
                
                return installations?.mapNotNull { installationElement ->
                    try {
                        val installation = installationElement.jsonObject
                        val account = installation["account"]?.jsonObject
                        
                        if (account != null) {
                            GitHubAppInstallation(
                                id = installation["id"]?.jsonPrimitive?.int ?: 0,
                                account = InstallationAccount(
                                    login = account["login"]?.jsonPrimitive?.content ?: "",
                                    id = account["id"]?.jsonPrimitive?.int ?: 0,
                                    type = account["type"]?.jsonPrimitive?.content ?: "Organization",
                                    avatar_url = account["avatar_url"]?.jsonPrimitive?.content ?: ""
                                ),
                                repository_selection = installation["repository_selection"]?.jsonPrimitive?.content ?: "all",
                                permissions = installation["permissions"]?.jsonObject ?: JsonObject(emptyMap()),
                                created_at = installation["created_at"]?.jsonPrimitive?.content ?: "",
                                updated_at = installation["updated_at"]?.jsonPrimitive?.content ?: "",
                                suspended = installation["suspended"]?.jsonPrimitive?.boolean ?: false
                            )
                        } else null
                    } catch (e: Exception) {
                        println("⚠️ Error parsing installation: ${e.message}")
                        null
                    }
                } ?: emptyList()
            } else {
                println("❌ Failed to fetch GitHub App installations: ${response.status}")
                return emptyList()
            }
        } catch (e: Exception) {
            println("🚫 Error fetching GitHub App installations: ${e.message}")
            return emptyList()
        }
    }
}

// Data classes for GraphQL communication
@Serializable
private data class GraphQLRequest(
    val query: String,
    val variables: Map<String, String?> = emptyMap()
)

@Serializable
data class GraphQLResponse(
    val data: JsonElement? = null,
    val errors: List<GraphQLErrorOriginal>? = null
)

@Serializable
data class GraphQLErrorOriginal(
    val message: String,
    val path: List<String>? = null,
    val locations: List<GraphQLLocation>? = null
)

@Serializable
data class GraphQLLocation(
    val line: Int,
    val column: Int
)

class GraphQLException(message: String, cause: Throwable? = null) : Exception(message, cause)
